import { ReactNode, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Draggable from "react-draggable";
import { PivotControl } from "../../../constants/pivot_control_options";

const COLLAPSE_KEY = "tcv.sidePanel.collapsed";
const WIDTH_KEY = "tcv.sidePanel.width";
const DEFAULT_WIDTH = 230;
const MIN_WIDTH = 220;
const MAX_WIDTH = 480;
const COLLAPSED_WIDTH = 52;

const clampWidth = (w: number) =>
  Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(w)));

const SIDE_PANEL_CONTROLS = [
  PivotControl.IncludeAtomicPurchasePlan,
  PivotControl.DateHierarchy,
  PivotControl.SegmentSelectMulti,
  PivotControl.Measure,
  PivotControl.PostFilterByHardcodedColumns,
  PivotControl.FilterBy,
  PivotControl.DatePeriodPicker,
  PivotControl.EditModeToggle,
];

/**
 * True when the view declares at least one control that renders in the rail.
 * Used to suppress the rail entirely for views with only header-level controls,
 * so there's no empty scaffolding.
 */
export function hasSidePanelControls(showControls: any[]): boolean {
  return SIDE_PANEL_CONTROLS.some((c) => showControls.includes(c));
}

export type ActiveSelections = {
  displayBy?: string;
  segments: string[];
  measure?: string;
  filterCount: number;
};

/**
 * Derives the active selections listed in the collapsed rail. Pure — reads only
 * the current params + the view's declared controls.
 */
export function getActiveSelections(args: {
  params: any;
  showControls: any[];
  dateHierarchyColumns: { name: string; caption: string }[];
  measureColumns: { name: string; caption: string }[];
  userFilters: any[];
}): ActiveSelections {
  const {
    params,
    showControls,
    dateHierarchyColumns,
    measureColumns,
    userFilters,
  } = args;
  const selections: ActiveSelections = { segments: [], filterCount: 0 };

  if (
    showControls.includes(PivotControl.DateHierarchy) &&
    params?.dateHierarchy
  ) {
    selections.displayBy =
      dateHierarchyColumns.find((c) => c.name === params.dateHierarchy)
        ?.caption ?? params.dateHierarchy;
  }
  if (showControls.includes(PivotControl.SegmentSelectMulti)) {
    selections.segments = (params?.segments ?? []).filter(
      (s: string) => s !== "table_id"
    );
  }
  if (showControls.includes(PivotControl.Measure) && params?.measure) {
    selections.measure =
      measureColumns.find((c) => c.name === params.measure)?.caption ??
      params.measure;
  }
  if (showControls.includes(PivotControl.FilterBy)) {
    selections.filterCount = userFilters?.length ?? 0;
  }
  return selections;
}

function CollapsedSummary({ selections }: { selections: ActiveSelections }) {
  const { displayBy, segments, measure, filterCount } = selections;
  const parts: string[] = [];
  if (displayBy) parts.push(displayBy);
  parts.push(...segments);
  if (measure) parts.push(measure);
  if (filterCount > 0) {
    parts.push(`${filterCount} filter${filterCount > 1 ? "s" : ""}`);
  }
  if (parts.length === 0) return null;

  // Vertical text running bottom-to-top, listing each active selection
  // (display-by, every applied segment, measure, filter count).
  return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
      <Box
        sx={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontSize: 12,
          color: "text.secondary",
          whiteSpace: "nowrap",
          letterSpacing: "0.02em",
        }}
      >
        {parts.join("  ·  ")}
      </Box>
    </Box>
  );
}

/**
 * Collapsible, resizable left rail for the data-shaping controls. The control
 * sections are passed as `children` (built in component.tsx where their handlers
 * live). Owns its collapsed boolean and expanded width, both persisted to
 * localStorage and kept local (not in any shared context) so toggling/resizing
 * never re-renders the table. Resize mirrors the Nucleus panel (react-draggable).
 */
export function ControlsSidePanel({
  selections,
  children,
}: {
  selections: ActiveSelections;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [width, setWidth] = useState<number>(() => {
    try {
      const stored = parseInt(localStorage.getItem(WIDTH_KEY) || "", 10);
      return Number.isFinite(stored) ? clampWidth(stored) : DEFAULT_WIDTH;
    } catch {
      return DEFAULT_WIDTH;
    }
  });
  const [isResizing, setIsResizing] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;
  const dragRef = useRef<HTMLDivElement>(null);

  const toggle = () =>
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });

  const handleResize = (_e: any, data: any) =>
    setWidth((w) => clampWidth(w + data.deltaX));

  const handleResizeStop = () => {
    setIsResizing(false);
    try {
      localStorage.setItem(WIDTH_KEY, String(widthRef.current));
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: collapsed ? COLLAPSED_WIDTH : width,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        backgroundColor: (t) => t.palette.atomic.surfaceTint,
        borderRight: "1px solid",
        borderColor: (t) => t.palette.atomic.border,
        transition: isResizing ? "none" : "width 0.2s ease",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 1,
          py: 0.5,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.01em",
              color: (t) => t.palette.atomic.inkSecondary,
              pl: 0.5,
            }}
          >
            Controls
          </Box>
        )}
        <Tooltip title={collapsed ? "Expand controls" : "Collapse controls"}>
          <IconButton size="small" onClick={toggle} aria-label="toggle controls">
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {collapsed ? <CollapsedSummary selections={selections} /> : children}
      </Box>

      {!collapsed && (
        <Draggable
          axis="x"
          onStart={() => setIsResizing(true)}
          onDrag={handleResize}
          onStop={handleResizeStop}
          position={{ x: 0, y: 0 }}
          nodeRef={dragRef}
        >
          <div
            ref={dragRef}
            className="drag-handle"
            style={{
              width: 6,
              position: "absolute",
              right: -3,
              top: 0,
              bottom: 0,
              zIndex: 1000,
              cursor: "col-resize",
              background: "rgba(148, 163, 184, 0.2)",
            }}
          />
        </Draggable>
      )}
    </Box>
  );
}
